import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ACTIVE_TASK_STATUSES,
  fetchRequesterTasks,
  TaskFilter,
  TaskHistoryItem,
} from '../../services/taskService';
import '../../styles/Account.css';

const PAGE_SIZE = 5;

const filters: Array<{ label: string; value: TaskFilter }> = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'All', value: 'ALL' },
  { label: 'New', value: 'NEW' },
  { label: 'Accepted', value: 'ACCEPTED' },
  { label: 'Arrived', value: 'ARRIVED' },
  { label: 'Navigating', value: 'NAVIGATING' },
  { label: 'In Progress', value: 'IN_PROGRESS' },
  { label: 'In Review', value: 'IN_REVIEW' },
  { label: 'Completed', value: 'COMPLETED' },
  { label: 'Cancelled', value: 'CANCELLED' },
  { label: 'Expired', value: 'EXPIRED' },
];

const statusLabel = (status?: string | null) =>
  (status || 'NEW')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const statusClass = (status?: string | null) => {
  switch ((status || '').toUpperCase()) {
    case 'NEW':
      return 'status-pending';
    case 'ACCEPTED':
      return 'status-accepted';
    case 'ARRIVED':
    case 'NAVIGATING':
    case 'IN_PROGRESS':
    case 'IN_REVIEW':
      return 'status-in-progress';
    case 'COMPLETED':
      return 'status-completed';
    case 'CANCELLED':
      return 'status-cancelled';
    case 'EXPIRED':
      return 'status-expired';
    default:
      return 'status-pending';
  }
};

const personName = (person: any, fallback: string) => {
  if (!person) return fallback;
  if (typeof person === 'string') return person;
  return (
    [person.firstName, person.middleName, person.lastName].filter(Boolean).join(' ') ||
    person.name ||
    person.email ||
    fallback
  );
};

const locationSummary = (location: any) => {
  if (!location) return '';
  return (
    [location.city, location.state, location.zipCode || location.zip, location.country]
      .filter(Boolean)
      .join(', ') ||
    location.address ||
    ''
  );
};

const formatMoney = (task: TaskHistoryItem) =>
  `${task.currency || 'USD'} ${(Number(task.tokenAmount || 0) + Number(task.tipAmount || 0)).toFixed(2)}`;

const emptyStateCopy = (filter: TaskFilter) => {
  if (filter === 'ACTIVE') {
    return {
      title: 'No active tasks',
      text: "You don't have any active requester tasks right now.",
    };
  }
  if (filter === 'ALL') {
    return {
      title: 'No tasks found',
      text: 'You do not have any requester tasks yet.',
    };
  }
  return {
    title: `No ${statusLabel(filter)} tasks`,
    text: `No ${statusLabel(filter)} tasks found in your history.`,
  };
};

const filterCountLabel = (filter: TaskFilter) => {
  if (filter === 'ACTIVE') return 'active';
  if (filter === 'ALL') return 'total';
  return statusLabel(filter).toLowerCase();
};

const MyTasks = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<TaskHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState<TaskFilter>('ACTIVE');
  const [page, setPage] = useState(0);
  const [isLastPage, setIsLastPage] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const requestIdRef = useRef(0);

  const loadTasks = useCallback(
    async (nextPage = 0, append = false, filter = activeFilter, forceRefresh = false) => {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      setError('');
      if (forceRefresh) setIsRefreshing(true);
      if (!append) {
        setTasks([]);
        setPage(0);
        setIsLastPage(true);
        setTotalCount(0);
      }

      try {
        const response = await fetchRequesterTasks(nextPage, PAGE_SIZE, filter, { forceRefresh });
        if (requestId !== requestIdRef.current) return;

        const nextTasks = response.content || [];
        setTasks((prev) => {
          const combined = append ? [...prev, ...nextTasks] : nextTasks;
          return Array.from(new Map(combined.map((task) => [task.id, task])).values());
        });
        setPage(response.number ?? nextPage);
        setIsLastPage(response.last ?? nextTasks.length < PAGE_SIZE);
        setTotalCount((prev) =>
          response.totalElements ?? (append ? prev + nextTasks.length : nextTasks.length)
        );
        if (!append) setLastRefreshedAt(new Date());
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        setError(err instanceof Error ? err.message : 'Failed to load tasks.');
        if (!append) setTasks([]);
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [activeFilter]
  );

  useEffect(() => {
    loadTasks(0, false, activeFilter);
  }, [activeFilter, loadTasks]);

  const emptyCopy = emptyStateCopy(activeFilter);
  const activeStatuses = useMemo(
    () => Array.from(ACTIVE_TASK_STATUSES).map(statusLabel).join(', '),
    []
  );

  return (
    <div className="account-container">
      <div className="account-header">
        <h1>My Tasks</h1>
        <p>Requester task history from the Olitask task service.</p>
      </div>

      {error && <div className="profile-message error">{error}</div>}

      <div className="account-card">
        <div className="section-header">
          <div>
            <h3>Task History</h3>
            <p className="section-subtext">
              Showing {tasks.length} {filterCountLabel(activeFilter)} task{tasks.length === 1 ? '' : 's'}
              {totalCount ? ` from ${totalCount} backend result${totalCount === 1 ? '' : 's'}` : ''}.
              {lastRefreshedAt ? ` Last refreshed ${lastRefreshedAt.toLocaleTimeString()}.` : ''}
            </p>
          </div>
          <button
            className="btn-add-card"
            type="button"
            onClick={() => loadTasks(0, false, activeFilter, true)}
            disabled={isLoading}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        <div className="task-filters">
          {filters.map((filter) => (
            <button
              key={filter.value}
              className={`filter-btn ${activeFilter === filter.value ? 'active' : ''}`}
              onClick={() => setActiveFilter(filter.value)}
              type="button"
              disabled={isLoading && activeFilter === filter.value}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {activeFilter === 'ACTIVE' && (
          <p className="task-filter-hint">Active includes {activeStatuses}.</p>
        )}

        {isLoading && tasks.length === 0 ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading your tasks...</p>
          </div>
        ) : tasks.length === 0 ? (
          <div className="empty-state">
            <h3>{emptyCopy.title}</h3>
            <p>{emptyCopy.text}</p>
            <button className="btn-create-task" type="button" onClick={() => navigate('/account')}>
              Back to Account
            </button>
          </div>
        ) : (
          <>
            <div className="tasks-list">
              {tasks.map((task) => {
                const location = locationSummary(task.location);
                const helper = personName(task.helper, 'Not assigned');
                const requester = personName(task.requester, 'Requester unavailable');
                return (
                  <div key={task.id} className="task-card">
                    <div className="task-header">
                      <h3>{task.title || task.taskType || 'Untitled Task'}</h3>
                      <span className={`status-badge ${statusClass(task.status)}`}>
                        {statusLabel(task.status)}
                      </span>
                    </div>
                    {task.description && <p className="task-description">{task.description}</p>}
                    <div className="task-details">
                      <div className="task-detail">
                        <strong>Created</strong>
                        <span>{task.createdAtDisplay || 'Date unavailable'}</span>
                      </div>
                      <div className="task-detail">
                        <strong>Offered</strong>
                        <span>{formatMoney(task)}</span>
                      </div>
                      <div className="task-detail">
                        <strong>Helper</strong>
                        <span>{helper}</span>
                      </div>
                      <div className="task-detail">
                        <strong>Requester</strong>
                        <span>{requester}</span>
                      </div>
                      {task.requesterDuration && (
                        <div className="task-detail">
                          <strong>ETD</strong>
                          <span>{task.requesterDuration}</span>
                        </div>
                      )}
                      {task.actualDuration && (
                        <div className="task-detail">
                          <strong>Duration</strong>
                          <span>{task.actualDuration}</span>
                        </div>
                      )}
                      {location && (
                        <div className="task-detail">
                          <strong>Location</strong>
                          <span>{location}</span>
                        </div>
                      )}
                      {task.counterOffers?.length ? (
                        <div className="task-detail">
                          <strong>Offers</strong>
                          <span>{task.counterOffers.length}</span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
            {!isLastPage && (
              <button
                className="btn-save"
                type="button"
                onClick={() => loadTasks(page + 1, true, activeFilter)}
                disabled={isLoading}
              >
                {isLoading ? 'Loading...' : 'Load More'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MyTasks;
